import { getDocument, PageViewport, PDFPageProxy } from 'pdfjs-dist';

import { type PageObjectUrls } from '../types';

export const convertPdfFileToImage = async (
  pdfFile: File,
): Promise<PageObjectUrls[]> => {
  if (!pdfFile) return [];

  try {
    // Convert File to ArrayBuffer for pdfjs-dist
    const arrayBuffer = await pdfFile.arrayBuffer();

    // Load PDF directly without DOM rendering
    const loadingTask = getDocument(arrayBuffer);
    const pdf = await loadingTask.promise;
    const numPages = pdf.numPages;

    const pagePreviews: PageObjectUrls[] = [];

    // Convert each page to image
    for (let pageNumber = 1; pageNumber <= numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const scaleDefaultRes = 2; // Higher scale for better quality of pdf preview
      const scaleHighRes = scaleDefaultRes * 1.2; // high res for click to zoom feature
      const viewportDefaultRes = page.getViewport({ scale: scaleDefaultRes });
      const viewportHighRes = page.getViewport({ scale: scaleHighRes });

      // Create off-screen canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Failed to get canvas context');
      }
      const defaultResObjectUrl = await viewportToObjectUrl(
        viewportDefaultRes,
        page,
        context,
        canvas,
      );
      const highResObjectUrl = await viewportToObjectUrl(
        viewportHighRes,
        page,
        context,
        canvas,
      );

      pagePreviews.push({
        defaultResObjectUrl,
        highResObjectUrl,
      });
    }

    return pagePreviews;
  } catch (error) {
    console.error('Error converting to image:', error);
    throw error;
  }
};

const viewportToObjectUrl = async (
  viewport: PageViewport,
  page: PDFPageProxy,
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
): Promise<string> => {
  canvas.height = viewport.height;
  canvas.width = viewport.width;

  // Render page to canvas
  await page.render({
    canvasContext: context,
    viewport,
    canvas,
  }).promise;

  // Convert to blob and create URL
  return new Promise<string>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to convert canvas to blob'));
          return;
        }

        resolve(URL.createObjectURL(blob));
      },
      'image/png',
      1.0,
    );
  });
};
